import React from 'react';
import PropTypes from 'prop-types';

/**
 * PropsTable — the single props table for the whole design system dashboard.
 *
 * Replaces eight hand-rolled tables that had drifted into three different
 * markups. Rows are authored by hand in each page's `meta`, and
 * `registry.test.js` cross-checks the names against the component's real
 * `propTypes` so a renamed or added prop fails the test suite.
 *
 * A row is a terse tuple — [name, type, default, description] — or an object
 * when it needs `required`.
 */

const normalizeRow = (row) => {
  if (Array.isArray(row)) {
    const [name, type, defaultValue, description] = row;
    return { name, type, default: defaultValue, description };
  }
  return row;
};

const PropsTable = ({ rows = [], title, component }) => {
  const normalized = rows.map(normalizeRow);

  return (
    <div>
      {title && (
        <h3 className="typo-h5 ds-page__card-title" style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
          {title}
        </h3>
      )}
      <div className="ds-page__table-scroll">
        {normalized.length === 0 ? (
          <p className="typo-body ds-page__table-empty">
            This component takes no props.
          </p>
        ) : (
          <table className="ds-page__table">
            <thead>
              <tr>
                <th className="typo-body-sm">Prop</th>
                <th className="typo-body-sm">Type</th>
                <th className="typo-body-sm">Default</th>
                <th className="typo-body-sm">Description</th>
              </tr>
            </thead>
            <tbody className="typo-body">
              {normalized.map((row) => (
                <tr key={row.name}>
                  <td>
                    <code className="ds-page__table-name">{row.name}</code>
                    {row.required && (
                      <abbr className="typo-body-sm ds-page__table-required" title="Required">*</abbr>
                    )}
                  </td>
                  <td className="ds-page__table-type">{row.type}</td>
                  <td>
                    <code className="ds-page__table-default">{row.default ?? '—'}</code>
                  </td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {component && (
        <p
          className="typo-body"
          style={{ paddingTop: 'var(--space-ui-small-l)', color: 'var(--ds-doc-ink-muted)' }}
        >
          Verified against <code>{component}.propTypes</code> by the props-drift test.
        </p>
      )}
    </div>
  );
};

PropsTable.propTypes = {
  /** Rows as [name, type, default, description] tuples, or objects with an optional `required` flag */
  rows: PropTypes.array,
  /** Optional heading, used when a page documents more than one prop set */
  title: PropTypes.string,
  /** Component name shown in the "verified by test" footnote */
  component: PropTypes.string,
};

export default PropsTable;
